/**
 * ---------------------------------------------------------------------------------------------------------------------
 * FRG INSTALLMENTS (display only)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Markup: sections/frg-installments.liquid, snippets/frg-installments-button.liquid. Styles: assets/frg-installments.css
 *
 * This file contains NO calculation, NO cart/checkout code and NO provider data. It only:
 *   1. <frg-installments-button>  copies the "Installments" button (configured in the section) next to the price.
 *   2. <frg-installments-periods> shows the rows of the selected period and hides providers without that period.
 *
 * Opening / closing the popup (button click, X, overlay, ESC, focus trap, scroll lock, mobile bottom sheet) is done
 * entirely by the theme's own <x-drawer> through the button's aria-controls attribute.
 */

const UPDATE_EVENT = 'frg-installments:update';

/* Button next to the price ------------------------------------------------------------------------------------------ */

class FrgInstallmentsButton extends HTMLElement {
  #source = null;
  #onUpdate = () => this.render();

  #onSectionUnload = () => requestAnimationFrame(() => this.render());

  connectedCallback() {
    this.#source = null;
    this.render();
    document.addEventListener(UPDATE_EVENT, this.#onUpdate);

    // Theme Editor: the Installments section was added, enabled, edited or removed.
    document.addEventListener('shopify:section:load', this.#onUpdate);
    document.addEventListener('shopify:section:unload', this.#onSectionUnload);
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATE_EVENT, this.#onUpdate);
    document.removeEventListener('shopify:section:load', this.#onUpdate);
    document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
  }

  render() {
    const template = document.querySelector('template[data-frg-installments-button]');
    const source = template ? template.innerHTML : '';

    // Only rebuild when the configured button really changed (theme editor). Rebuilding needlessly would replace the
    // element the drawer returns focus to, and reset its aria-expanded state.
    if (source === this.#source) {
      return;
    }

    this.#source = source;
    this.replaceChildren(template ? template.content.cloneNode(true) : '');
  }
}

/* Period tiles ----------------------------------------------------------------------------------------------------- */

class FrgInstallmentsPeriods extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    // Runs again when the theme moves the drawer to <body> on open: rebind without duplicating listeners.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        this.select(event.target.value);
      }
    }, { signal: this.#abortController.signal });

    const radios = Array.from(this.querySelectorAll('input[type="radio"]'));
    const current = radios.find((radio) => radio.checked) || radios[0];

    if (current) {
      current.checked = true;
      this.select(current.value);
    }

    // Theme editor: the section was (re)rendered, refresh the button next to the price.
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT));
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    // Theme editor: if the section was removed, the button next to the price must disappear too.
    requestAnimationFrame(() => document.dispatchEvent(new CustomEvent(UPDATE_EVENT)));
  }

  select(period) {
    const popup = this.closest('.frg-installments');

    if (!popup) {
      return;
    }

    let anyProviderVisible = false;

    popup.querySelectorAll('.frg-installments__provider').forEach((provider) => {
      let providerVisible = false;

      provider.querySelectorAll('tr[data-period]').forEach((row) => {
        const match = row.dataset.period === String(period);
        row.hidden = !match;
        providerVisible ||= match;
      });

      provider.hidden = !providerVisible;
      anyProviderVisible ||= providerVisible;
    });

    const empty = popup.querySelector('.frg-installments__empty');

    if (empty) {
      empty.hidden = anyProviderVisible;
    }
  }
}

if (!window.customElements.get('frg-installments-button')) {
  window.customElements.define('frg-installments-button', FrgInstallmentsButton);
}

if (!window.customElements.get('frg-installments-periods')) {
  window.customElements.define('frg-installments-periods', FrgInstallmentsPeriods);
}